import { useCallback, useEffect, useState } from 'react';
import { useApp } from '../store/app';
import {
  SKIP_BACK_SEC,
  SKIP_FORWARD_SEC,
  SPEEDS,
  activeChapterIndex,
  jumpToChapter,
  nextTrack,
  openBook,
  previousTrack,
  seekTo,
  setSleepAtChapterEnd,
  setSleepTimer,
  setSpeed,
  skipBack,
  skipForward,
  togglePlay,
} from '../player/engine';
import {
  absolutePosition,
  chapterDuration,
  effectiveChapters,
  knownTotalDuration,
} from '../lib/bookMath';
import { formatRemaining, formatTime } from '../lib/format';
import { accentFromImage, fallbackAccent } from '../lib/color';
import { Cover, NmIconButton, NmPill, NmPlayButton, NmSeekbar, ProgressLine } from './nm';
import {
  AlertIcon,
  BackIcon,
  ListIcon,
  MoonIcon,
  NextIcon,
  PrevIcon,
  SkipBackIcon,
  SkipForwardIcon,
  SpeedIcon,
} from './icons';

type Panel = 'none' | 'speed' | 'sleep' | 'chapters';

const SLEEP_OPTIONS = [15, 30, 45, 60] as const;

export function Player({ bookId }: { bookId: string }) {
  const book = useApp((s) => s.books.find((b) => b.id === bookId) ?? null);
  const libraryStatus = useApp((s) => s.libraryStatus);
  const currentBookId = useApp((s) => s.currentBookId);
  const navigate = useApp((s) => s.navigate);

  const rawChapters = useApp((s) => s.chapters);
  const trackIndex = useApp((s) => s.trackIndex);
  const positionSec = useApp((s) => s.positionSec);
  const trackDurationSec = useApp((s) => s.trackDurationSec);
  const playing = useApp((s) => s.playing);
  const buffering = useApp((s) => s.buffering);
  const speed = useApp((s) => s.speed);
  const playbackError = useApp((s) => s.playbackError);

  const [panel, setPanel] = useState<Panel>('none');
  const [accent, setAccent] = useState<string>(() => fallbackAccent(bookId));

  // Centres the current chapter when the list opens, and follows along as
  // playback moves on. A callback ref rather than an effect, because the row
  // only exists while the panel is mounted. Scrolls the list itself instead of
  // scrollIntoView, which would also drag the whole page.
  const centreActiveChapter = useCallback((node: HTMLLIElement | null) => {
    const list = node?.parentElement;
    if (!node || !list) return;
    list.scrollTop = node.offsetTop - list.clientHeight / 2 + node.clientHeight / 2;
  }, []);

  // Deep-linking straight to /#/player/<id> has to work, so load the book if
  // the engine is not already on it.
  useEffect(() => {
    if (book && currentBookId !== book.id) void openBook(book, { autoplay: false });
  }, [book, currentBookId]);

  // Tint the ambient glow with the cover's own colour.
  const coverUrl = book?.coverUrl;
  const title = book?.title;
  useEffect(() => {
    if (!title) return;
    if (!coverUrl) {
       setAccent(fallbackAccent(title));
       return;
    }
    let cancelled = false;
    void accentFromImage(coverUrl, title).then((value) => {
       if (!cancelled) setAccent(value);
    });
    return () => {
       cancelled = true;
    };
  }, [coverUrl, title]);

  if (!book) {
    return (
       <div className="notice nm-raised">
         <AlertIcon size={22} />
         <div>
           <h2 className="notice__title">
              {libraryStatus === 'loading' ? 'Loading…' : 'That book is no longer in your library'}
           </h2>
           {libraryStatus !== 'loading' && (
              <button type="button" className="linkbtn" onClick={() => navigate({ name: 'library' })}>
                Back to library
              </button>
           )}
         </div>
       </div>
        );
    }

    const chapters = effectiveChapters(book, rawChapters);
    const chapterIdx = activeChapterIndex(chapters, trackIndex, positionSec);
    const chapter = chapterIdx >= 0 ? chapters[chapterIdx] : undefined;

    // Single-file books scrub within the chapter; multi-file books scrub within
    // the file, which for those layouts is the same thing.
    const chapterStart = chapter?.startSec ?? 0;
    const chapterLen =
      chapterIdx >= 0 ? chapterDuration(book, chapters, chapterIdx) : trackDurationSec;
    const withinChapter = Math.max(0, positionSec - chapterStart);
    const scrubMax = chapterLen > 0 ? chapterLen : trackDurationSec;

    const bookTotal = knownTotalDuration(book);
    const bookPos = absolutePosition(book, trackIndex, positionSec);
    const bookRatioNow = bookTotal > 0 ? bookPos / bookTotal : 0;

    const isFirst = trackIndex === 0 && positionSec < 3 && chapterIdx <= 0;
    const isLast = trackIndex === book.tracks.length - 1 && chapterIdx === chapters.length - 1;

    return (
      <div className="player" style={{ '--book-accent': accent } as React.CSSProperties}>
        <div className="player__glow" aria-hidden="true" />

          <div className="player__head">
            <NmIconButton label="Back to library" onClick={() => navigate({ name: 'library' })}>
              <BackIcon size={20} />
            </NmIconButton>
            <span className="player__eyebrow">Now playing</span>
          </div>

          <div className="player__art nm-inset" data-playing={playing}>
            <Cover title={book.title} url={book.coverUrl} className="player__cover" />
          </div>

          <div className="player__titles">
            <h1 className="player__title">{book.title}</h1>
            {book.author && <p className="player__author">{book.author}</p>}
            {chapter && <p className="player__chapter">{chapter.title}</p>}
          </div>

          {playbackError && (
             <div className="nm-raised notice notice--error player__error">
               <AlertIcon size={20} />
               <p className="notice__body">{playbackError}</p>
             </div>
          )}

          <div className="player__scrub">
            <NmSeekbar
               label="Seek within chapter"
               value={withinChapter}
               max={scrubMax}
               disabled={scrubMax <= 0}
               onSeek={(v) => seekTo(chapterStart + v)}
            />
            <div className="player__times tnum">
               <span>{formatTime(withinChapter)}</span>
               <span className="player__times-right">-{formatTime(Math.max(0, scrubMax - withinChapter))}</span>
            </div>
          </div>

          <div className="player__transport">
            <NmIconButton
              label="Previous chapter"
              onClick={() => void previousTrack()}
              disabled={isFirst}
            >
              <PrevIcon size={20} />
            </NmIconButton>

             <NmIconButton label={`Back ${SKIP_BACK_SEC} seconds`} size={54} onClick={() => skipBack()}>
               <SkipBackIcon size={25} seconds={SKIP_BACK_SEC} />
             </NmIconButton>

             <NmPlayButton
                playing={playing}
                busy={buffering}
                progress={scrubMax > 0 ? withinChapter / scrubMax : 0}
                onClick={() => void togglePlay()}
             />

             <NmIconButton
          label={`Forward ${SKIP_FORWARD_SEC} seconds`}
          size={54}
          onClick={() => skipForward()}
      >
        <SkipForwardIcon size={25} seconds={SKIP_FORWARD_SEC} />
      </NmIconButton>

      <NmIconButton label="Next chapter" onClick={() => void nextTrack()} disabled={isLast}>
        <NextIcon size={20} />
      </NmIconButton>
    </div>

    <div className="player__tools">
      <NmPill
        active={panel === 'speed'}
        onClick={() => setPanel(panel === 'speed' ? 'none' : 'speed')}
        aria-expanded={panel === 'speed'}
      >
        <SpeedIcon size={17} />
        <span className="tnum">{speed}×</span>
      </NmPill>

      <SleepPill active={panel === 'sleep'} onToggle={() => setPanel(panel === 'sleep' ? 'none' : 'sleep')} />

      <NmPill
        active={panel === 'chapters'}
        onClick={() => setPanel(panel === 'chapters' ? 'none' : 'chapters')}
        aria-expanded={panel === 'chapters'}
      >
        <ListIcon size={17} />
        <span>
           {chapters.length} {chapters.length === 1 ? 'chapter' : 'chapters'}
        </span>
      </NmPill>
    </div>

    {panel === 'speed' && (
       <div className="nm-inset panel">
         <p className="panel__label">Playback speed</p>
         <div className="panel__row">
           {SPEEDS.map((s) => (
              <NmPill key={s} active={speed === s} onClick={() => setSpeed(s)}>
                <span className="tnum">{s}×</span>
              </NmPill>
           ))}
         </div>
       </div>
    )}

    {panel === 'sleep' && <SleepPanel />}

    {panel === 'chapters' && (
       <div className="nm-inset panel">
         <p className="panel__label">Chapters</p>
         <ul className="chapters">
           {chapters.map((c, i) => (
              <li
                key={`${c.trackIndex}-${c.startSec}-${i}`}
                ref={i === chapterIdx ? centreActiveChapter : undefined}
                style={{ '--i': Math.min(i, 12) } as React.CSSProperties}
              >
                <button
                  type="button"
                  className="nm-press chapters__item"
                  data-active={i === chapterIdx || undefined}
                  aria-current={i === chapterIdx || undefined}
                  onClick={() => void jumpToChapter(c)}
                >
                  <span className="chapters__num tnum">{String(i + 1).padStart(2, '0')}</span>
                  <span className="chapters__title">{c.title}</span>
                  <span className="chapters__len tnum">
                    {formatTime(chapterDuration(book, chapters, i))}
                  </span>
                </button>
              </li>
           ))}
         </ul>
       </div>
    )}

    <div className="player__book-progress">
      <ProgressLine ratio={bookRatioNow} />
      <div className="player__times player__times--dim tnum">
        <span>
          {bookTotal > 0 ?
                `${formatTime(bookPos)} of ${formatTime(bookTotal)}`
              : 'Whole-book length unknown until each file loads'}
           </span>
           {bookTotal > 0 && (
              <span className="player__times-right">
                {formatRemaining(bookTotal - bookPos)}
              </span>
           )}
        </div>
      </div>
    </div>
  );
}

/* ---------------- sleep timer ---------------- */

function SleepPill({ active, onToggle }: { active: boolean; onToggle: () => void }) {
  const sleepEndsAt = useApp((s) => s.sleepEndsAt);
  const atChapterEnd = useApp((s) => s.sleepAtChapterEnd);
  const remaining = useCountdown(sleepEndsAt);

  const label =
     sleepEndsAt !== null ? formatTime(remaining)
     : atChapterEnd ? 'Chapter end'
     : 'Sleep';

  return (
     <NmPill active={active} onClick={onToggle} aria-expanded={active}>
       <MoonIcon size={17} />
       <span className={sleepEndsAt !== null ? 'tnum' : undefined}>{label}</span>
     </NmPill>
  );
}

function SleepPanel() {
  const sleepEndsAt = useApp((s) => s.sleepEndsAt);
  const atChapterEnd = useApp((s) => s.sleepAtChapterEnd);
  const remaining = useCountdown(sleepEndsAt);
  const armed = sleepEndsAt !== null || atChapterEnd;

  return (
     <div className="nm-inset panel">
       <p className="panel__label">
         Sleep timer
         {sleepEndsAt !== null && (
            <span className="panel__hint tnum"> · fades out in {formatTime(remaining)}</span>
         )}
       </p>
       <div className="panel__row">
         {SLEEP_OPTIONS.map((m) => (
            <NmPill key={m} onClick={() => setSleepTimer(m)}>
              {m} min
            </NmPill>
         ))}
         <NmPill active={atChapterEnd} onClick={() => setSleepAtChapterEnd(!atChapterEnd)}>
            End of chapter
         </NmPill>
         <NmPill onClick={() => setSleepTimer(null)} disabled={!armed}>
            Off
         </NmPill>
       </div>
     </div>
  );
}

/** Seconds left on the sleep timer, re-rendering once a second while armed. */
function useCountdown(endsAt: number | null): number {
  const [, force] = useState(0);

  useEffect(() => {
     if (endsAt === null) return;
     const id = setInterval(() => force((n) => n + 1), 1000);
     return () => clearInterval(id);
  }, [endsAt]);

  return endsAt === null ? 0 : Math.max(0, (endsAt - Date.now()) / 1000);
}
